/**
 * buildHblPrintDocumentHtml
 *
 * Mirrors the exact same pattern used by buildBookingPrintDocumentHtml in BookingPrint.
 * Produces a standalone HTML string with:
 *   - <thead> containing `.hbl-print-header-cell`  → repeats on every printed page
 *   - <tbody> containing `.hbl-print-body-cell`    → main content, paginated naturally
 *   - <tfoot> containing `.hbl-print-footer-cell`  → repeats on every printed page
 *
 * The PDF generator (captureHblPdfLayersFromPrintShell) renders this in a hidden
 * iframe, then captures each region as a separate canvas so the multi-page PDF
 * can stamp header + footer on every page while slicing the body correctly.
 *
 * @param {string} headerOuterHtml   - outerHTML of the `.hbl-print-header-block` element
 * @param {string} bodyOuterHtml     - outerHTML of the main body content (everything except header/footer)
 * @param {string} footerOuterHtml   - outerHTML of the footer element
 * @param {string} documentTitle     - used for <title>
 */
export function buildHblPrintDocumentHtml(
  headerOuterHtml,
  bodyOuterHtml,
  footerOuterHtml,
  documentTitle = 'House Bill of Lading',
) {
  // Collect all app stylesheets so the iframe renders with the real Tailwind/app CSS.
  const inlinedCss = Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        return Array.from(sheet.cssRules || [])
          .map((r) => r.cssText)
          .join('\n');
      } catch {
        return sheet.href ? `@import url("${sheet.href}");` : '';
      }
    })
    .join('\n');

  const linkFallback = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .map((n) => n.outerHTML)
    .join('\n');

  // Forward CSS custom properties (design tokens) from the live page.
  const rootEl = document.documentElement;
  const rootStyles = window.getComputedStyle(rootEl);
  const customProps = [];
  for (let i = 0; i < rootStyles.length; i++) {
    const name = rootStyles[i];
    if (name.startsWith('--')) {
      customProps.push(`${name}: ${rootStyles.getPropertyValue(name).trim()};`);
    }
  }
  const rootVarsCss = `:root { ${customProps.join(' ')} }`;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${documentTitle}</title>
  <style>${rootVarsCss}</style>
  <style>${inlinedCss}</style>
  ${linkFallback}
  <style>
    /* ── Page setup ── */
    /*
     * LAYOUT WIDTH FIX (Issue #2-B):
     * Browser print uses @page { margin: 8mm } → content area = 210mm - 16mm = 194mm.
     * jsPDF uses margin=8 on each side → mmWidth = pdfWidth - 8*2 = 194mm.
     * The iframe must render at 194mm content width so the PDF canvas matches
     * print preview exactly — no compression or stretching artefact.
     * We set body padding: 0 and width: 194mm so both paths share one source of truth.
     */
    @page { size: A4; margin: 8mm; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    /* ── OUTER BORDER FIX (Issue #1) ──────────────────────────────────────────
     *
     * ROOT CAUSE: border-collapse:collapse on .hbl-print-table causes the
     * outermost <td> cell edges to render a 1px collapsed border in some
     * browsers/html2canvas, even when border:none is declared on the <td>.
     * Explicitly zero every possible border/outline/shadow on all print
     * structural elements. Internal BOL borders live INSIDE bodyOuterHtml
     * (on .hbl-main-grid and cargo grid divs) and are completely unaffected.
     */
    .hbl-print-table,
    .hbl-print-table > thead,
    .hbl-print-table > tbody,
    .hbl-print-table > tfoot,
    .hbl-print-table > thead > tr,
    .hbl-print-table > tbody > tr,
    .hbl-print-table > tfoot > tr,
    .hbl-print-table > thead > tr > td,
    .hbl-print-table > tbody > tr > td,
    .hbl-print-table > tfoot > tr > td {
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
    }

    /* ── Print-table layout (same trick as BookingPrint) ── */
    .hbl-print-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    /* thead/tfoot with display:table-*-group makes the browser
       repeat those rows on every printed page automatically. */
    thead.hbl-print-thead { display: table-header-group;border-bottom: 1px solid #000; }
    tfoot.hbl-print-tfoot { display: table-footer-group; }

   .hbl-print-header-cell,
.hbl-print-body-cell,
.hbl-print-footer-cell {
  outline: none !important;
  box-shadow: none !important;
  vertical-align: top;
}

.hbl-print-body-cell {
  border: none !important;
  padding: 0;
}

.hbl-print-header-cell {
  border-bottom: 1px solid #000 !important;
  padding-bottom: 4px;
}

.hbl-print-footer-cell {
  border-top: 1px solid #000 !important;
  padding-top: 4px;
}

    /* ── Wrapper for the whole document ── */
    .hbl-print-doc {
      /*
       * LAYOUT WIDTH FIX (Issue #2-B cont.):
       * Width set to 194mm = 210mm A4 - 8mm left margin - 8mm right margin.
       * This is the true printable content width. Both jsPDF (mmWidth=194mm)
       * and browser @page (margin:8mm each side) use this same 194mm.
       * Removed width:210mm that caused the 92.4% shrink artefact in PDF.
       */
      width: 194mm;
      box-sizing: border-box;
      font-family: Arial, sans-serif;
      background: #fff;
      border: none !important;
      box-shadow: none !important;
      outline: none !important;
    }

    /* ── Gutter wrapper — note: NO extra padding, content already at 194mm ── */
    .hbl-print-gutter {
      box-sizing: border-box;
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
    }

    .bol-value {
      color: #000 !important;
      font-weight: 700 !important;
      font-size: 10px !important;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      line-height: 1.3;
    }

    .hbl-paragraph,
    .hbl-paragraph * {
      text-transform: none !important;
      font-weight: 400 !important;
      letter-spacing: normal !important;
    }

    .text-xs {
      color: #1f2937;
      font-size: 11px;
      line-height: 1.3;
    }

    /* Charges table */
    .hbl-charges-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      margin: 0;
    }
    .hbl-charges-table th,
    .hbl-charges-table td {
      border: 1px solid #000;
      padding: 3px 6px;
      vertical-align: middle;
      line-height: 1.25;
      text-align: center;
    }
    .hbl-charges-table thead th {
      background-color: #d9d9d9 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color: #000;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      font-size: 9px;
    }
    .hbl-charges-table tbody td {
      color: #1f2937;
      font-size: 10px;
    }
    .hbl-charges-table tbody td:first-child { text-align: left; }
    .hbl-charges-table tfoot td {
      background-color: #f3f3f3 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color: #000;
      font-weight: 800;
      font-size: 11px;
    }
    .hbl-charges-table tfoot td:first-child {
      text-align: right;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }

    /* Cargo particulars table — thead repeats on each printed page */
    .hbl-cargo-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    .hbl-cargo-table thead {
      display: table-header-group;
    }
    .hbl-cargo-table thead th {
      border-bottom: 1px solid #000;
      border-right: 1px solid #000;
      padding: 4px;
      text-align: center;
      vertical-align: middle;
    }
    .hbl-cargo-table thead th:last-child {
      border-right: none;
    }
    .hbl-cargo-table tbody td {
      border-right: 1px dashed #000;
      padding: 4px;
      vertical-align: top;
    }
    .hbl-cargo-table tbody td:last-child {
      border-right: none;
    }
    .hbl-cargo-table tbody tr {
      page-break-inside: auto;
      break-inside: auto;
    }

    /* Page-break guard: keep these blocks together */
    .hbl-bottom-section      { page-break-inside: avoid; break-inside: avoid; }
    .hbl-charges-footer-row  { page-break-inside: avoid; break-inside: avoid; }
    .hbl-signature-block     { page-break-inside: avoid; break-inside: avoid; }
    .hbl-footer-row          { page-break-inside: avoid; break-inside: avoid; }

    /* Watermark hidden in print */
    .watermark { display: none !important; }

    /* Tailwind print utilities */
    .print\\:hidden   { display: none !important; }
    .print\\:bg-white { background: white !important; }

    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  </style>
</head>
<body>
  <div class="hbl-print-doc">
    <table class="hbl-print-table">

      <!-- HEADER — repeats on every printed page -->
      <thead class="hbl-print-thead">
        <tr>
          <td class="hbl-print-header-cell">
            <div class="hbl-print-gutter">
              ${headerOuterHtml}
            </div>
          </td>
        </tr>
      </thead>

      <!-- FOOTER — repeats on every printed page -->
      <tfoot class="hbl-print-tfoot">
        <tr>
          <td class="hbl-print-footer-cell">
            <div class="hbl-print-gutter">
              ${footerOuterHtml}
            </div>
          </td>
        </tr>
      </tfoot>

      <!-- BODY — paginated naturally -->
      <tbody class="hbl-print-tbody">
        <tr>
          <td class="hbl-print-body-cell">
            <div class="hbl-print-gutter">
              ${bodyOuterHtml}
            </div>
          </td>
        </tr>
      </tbody>

    </table>
  </div>
</body>
</html>`;
}